
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "../api/axiosAuth";
import { io } from "socket.io-client";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useAuth } from "../context/AuthContext";
import { API } from "../components/config";
import "../style/HorarioProfesor.css";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];

export default function HorarioProfesor({ profesorId: propProfesorId }) {
  const { user, token } = useAuth();

  const profesorId = propProfesorId || user?.id;
  const tenantId = user?.tenant_id;
  const sedeId = user?.sede_id;

  const api = useMemo(() => axios, []);

  const [horario, setHorario] = useState([]);
  // horas ahora es una lista de bloques exactos: [{ inicio: "06:30", fin: "07:30" }, ...]
  const [horas, setHoras] = useState([]);

  /* =====================================
     FORMATO DE HORA (12h, es-CO)
  ===================================== */

  const formatHora12 = useCallback((hora24) => {
    if (!hora24) return "";
    const [h, m] = hora24.split(":").map(Number);
    const ampm = h >= 12 ? "p.m." : "a.m.";
    const hora = h % 12 || 12;
    return `${hora}:${String(m).padStart(2, "0")} ${ampm}`;
  }, []);

  const formatRango = useCallback(
    (inicio, fin) => `${formatHora12(inicio)} - ${formatHora12(fin)}`,
    [formatHora12]
  );

  /* =====================================
     CONSTRUCCIÓN DE BLOQUES DE HORA
     A partir de las horas reales de inicio/fin
     de las clases (respeta 6:30, 7:15, etc.)
  ===================================== */

  const procesarHoras = useCallback((lista = []) => {
    const marcas = new Set();

    lista.forEach((item) => {
      if (!item.hora_inicio || !item.hora_fin) return;
      marcas.add(item.hora_inicio.substring(0, 5));
      marcas.add(item.hora_fin.substring(0, 5));
    });

    const ordenadas = Array.from(marcas).sort();

    const bloques = [];
    for (let i = 0; i < ordenadas.length - 1; i++) {
      bloques.push({ inicio: ordenadas[i], fin: ordenadas[i + 1] });
    }

    setHoras(bloques);
  }, []);

  /* ===================================== */

  const cargarHorario = useCallback(async () => {
    if (!profesorId) return;

    try {
      const { data } = await api.get(`/horario/${profesorId}`);
      const lista = Array.isArray(data) ? data : [];

      const ordenDias = { Lunes: 1, Martes: 2, Miércoles: 3, Jueves: 4, Viernes: 5, Sábado: 6 };

      lista.sort((a, b) => {
        if (ordenDias[a.dia] !== ordenDias[b.dia]) {
          return ordenDias[a.dia] - ordenDias[b.dia];
        }
        return a.hora_inicio.localeCompare(b.hora_inicio);
      });

      setHorario(lista);
      procesarHoras(lista);
    } catch (err) {
      console.error(err);
    }
  }, [api, profesorId, procesarHoras]);

  /* =====================================
     SOCKET
  ===================================== */

  useEffect(() => {
    if (!token || !profesorId) return;

    cargarHorario();

    const socket = io(API, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
      console.log("✅ Socket conectado");
      socket.emit("join-profesor", { profesorId, tenantId, sedeId });
    });

    socket.on("horario-actualizado", (data) => {
      const lista = Array.isArray(data) ? data : [];
      setHorario(lista);
      procesarHoras(lista);
    });

    socket.on("connect_error", (err) => {
      console.error("Socket Error:", err.message);
    });

    socket.on("disconnect", () => {
      console.log("Socket desconectado");
    });

    return () => {
      socket.disconnect();
    };
  }, [token, profesorId, tenantId, sedeId, cargarHorario, procesarHoras]);

  /* =====================================
     BUSCAR CLASE QUE CUBRE UN BLOQUE
  ===================================== */

  const getClase = useCallback(
    (dia, bloque) => {
      return horario.find((item) => {
        if (item.dia !== dia || !item.hora_inicio || !item.hora_fin) return false;
        const inicio = item.hora_inicio.substring(0, 5);
        const fin = item.hora_fin.substring(0, 5);
        return inicio <= bloque.inicio && fin >= bloque.fin;
      });
    },
    [horario]
  );

  /* =====================================
     GRID POR DÍA CON CELDAS FUSIONADAS
     (una clase que ocupa varios bloques
     seguidos se pinta como una sola celda)
  ===================================== */

  const gridPorDia = useMemo(() => {
    const resultado = {};

    DIAS.forEach((dia) => {
      const columna = new Array(horas.length).fill(null);
      let i = 0;

      while (i < horas.length) {
        const clase = getClase(dia, horas[i]);
        let j = i + 1;

        while (j < horas.length && getClase(dia, horas[j]) === clase) {
          j++;
        }

        columna[i] = { clase, span: j - i };
        for (let k = i + 1; k < j; k++) columna[k] = { skip: true };

        i = j;
      }

      resultado[dia] = columna;
    });

    return resultado;
  }, [horas, getClase]);

  /* =====================================
     TOTALES (en minutos reales, no horas enteras)
  ===================================== */

  const totalSemanal = useMemo(() => {
    const minutos = horario.reduce((sum, item) => {
      if (!item.hora_inicio || !item.hora_fin) return sum;
      const [hi, mi] = item.hora_inicio.split(":").map(Number);
      const [hf, mf] = item.hora_fin.split(":").map(Number);
      return sum + (hf * 60 + mf - (hi * 60 + mi));
    }, 0);
    return minutos / 60;
  }, [horario]);

  const totalMensual = totalSemanal * 4;

  /* ===================================== */

  const exportPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Horario del Profesor", 15, 15);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Generado el ${new Date().toLocaleDateString("es-CO")}`, 15, 21);

    autoTable(doc, {
      startY: 27,
      head: [["Hora", ...DIAS]],
      body: horas.map((bloque) => [
        formatRango(bloque.inicio, bloque.fin),
        ...DIAS.map((dia) => {
          const clase = getClase(dia, bloque);
          if (!clase) return "-";
          const linea2 = `${clase.grado}-${(clase.grupo || "").toUpperCase()}`;
          return clase.aula
            ? `${clase.materia}\n${linea2}\n${clase.aula}`
            : `${clase.materia}\n${linea2}`;
        }),
      ]),
      styles: { fontSize: 8, cellPadding: 3, valign: "middle", halign: "center" },
      headStyles: { fillColor: [27, 58, 87], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 249, 251] },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 26 } },
    });

    doc.save("HorarioProfesor.pdf");
  };

  /* ===================================== */

  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Hora", ...DIAS],
      ...horas.map((bloque) => [
        formatRango(bloque.inicio, bloque.fin),
        ...DIAS.map((dia) => {
          const clase = getClase(dia, bloque);
          if (!clase) return "-";
          const linea2 = `${clase.grado}-${(clase.grupo || "").toUpperCase()}`;
          return clase.aula ? `${clase.materia} ${linea2} (${clase.aula})` : `${clase.materia} ${linea2}`;
        }),
      ]),
    ]);

    ws["!cols"] = [{ wch: 18 }, ...DIAS.map(() => ({ wch: 24 }))];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Horario");
    XLSX.writeFile(wb, "HorarioProfesor.xlsx");
  };

  /* ===================================== */

  return (
    <div className="horario-container">
      <header className="horario-header">
        <div className="horario-titulo">
          <h2>Horario del Profesor</h2>
          <p className="horario-subtitulo">Distribución semanal de clases asignadas</p>
        </div>

        <div className="horario-stats">
          <div className="stat-card">
            <span className="stat-valor">{totalSemanal.toFixed(1)}h</span>
            <span className="stat-label">Semanal</span>
          </div>
          <div className="stat-card">
            <span className="stat-valor">{totalMensual.toFixed(1)}h</span>
            <span className="stat-label">Mensual</span>
          </div>
        </div>
      </header>

      <div className="horario-acciones">
        <button className="btn btn-pdf" onClick={exportPDF}>
          Exportar PDF
        </button>
        <button className="btn btn-excel" onClick={exportExcel}>
          Exportar Excel
        </button>
      </div>

      <div className="tabla-wrapper">
        <table className="tabla-horario">
          <thead>
            <tr>
              <th className="col-hora">Hora</th>
              {DIAS.map((d) => (
                <th key={d}>{d}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {horas.length === 0 ? (
              <tr>
                <td colSpan={DIAS.length + 1} className="sin-horario">
                  Este profesor aún no tiene horario asignado
                </td>
              </tr>
            ) : (
              horas.map((bloque, rowIdx) => (
                <tr key={`${bloque.inicio}-${bloque.fin}`}>
                  <td className="col-hora">{formatRango(bloque.inicio, bloque.fin)}</td>

                  {DIAS.map((dia) => {
                    const celda = gridPorDia[dia][rowIdx];
                    if (!celda || celda.skip) return null;

                    const { clase, span } = celda;

                    return (
                      <td
                        key={dia}
                        rowSpan={span}
                        className={clase ? "celda-clase" : "celda-vacia"}
                      >
                        {clase ? (
                          <div className="clase-info">
                            <span className="materia">{clase.materia}</span>
                            <span className="grado-grupo">
                              {clase.grado}-{(clase.grupo || "").toUpperCase()}
                            </span>
                            {clase.aula && <span className="aula">{clase.aula}</span>}
                          </div>
                        ) : (
                          <span className="vacio-guion">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}